/**
 * Block 14 «Раскрой» — PR #1 EP-1 (CUT-1.1). Фабрики дефолтных значений.
 *
 * Дефолты настроек — вербатим из TRD §4.1 (kerf 4 / edgeOffset 10 /
 * edgeOverhang 50 / startPoint 'tl' / preferLong/Wide true). Получаем их через
 * `CuttingSettingsSchema.parse({...})`, чтобы источником истины оставалась схема.
 *
 * ИСКЛЮЧЕНИЕ — `method`: UI-дефолт = `'length'` (прототип project.html:108 рисует
 * `active` на кнопке «По длине», источник истины UI). Схема/контракт ADR-0065
 * сохраняют `.default('optimal')` НЕ трогаем — это fallback для ОТСУТСТВУЮЩЕГО
 * поля; стор всегда инициализируется явным 'length' и шлёт значение явно, конфликта
 * на практике нет. Косметическое расхождение contract-default↔UI-default — BACKLOG
 * B-212, выровнять при контракт-правке EP-3.
 *
 * Размер листа НЕ задаём по умолчанию (в доках нет дефолтного размера —
 * §13 handover: «чего нет в доках — не выдумывать»). `mode: 'uniform'` с
 * пустым `uniformSheet` валиден (uniformSheet опционален); конкретный размер
 * пользователь выберет на экране Настройки (PR #3–#6, §IX.X #13).
 */

import { CuttingSettingsSchema, CuttingDocumentSchema } from './schemas';
import type { CuttingSettings, CuttingDocument, SheetSizeConfig } from './types';

/** Дефолтная конфигурация листа: uniform, размер выбирается пользователем позже. */
export const DEFAULT_SHEET_CONFIG: SheetSizeConfig = { mode: 'uniform' };

/**
 * Канонический UI-дефолт размера листа (2750×1830 — вербатим `project.html`
 * sheet-size-label + `SheetSizeDialog.DEFAULT_UNIFORM`). Единый источник: его же
 * подставляет `buildEngineInput`, если пользователь не задал размер (иначе движок
 * `resolveSheetSpec` бросает на `uniform` без `uniformSheet` — баг PR #23, B-212).
 * НЕ кладётся в `DEFAULT_SHEET_CONFIG` (контракт-дефолт намеренно без размера, см.
 * шапку) — это fallback ТОЛЬКО на границе движка.
 */
export const DEFAULT_UNIFORM_SHEET = { length: 2750, width: 1830 } as const;

/** Дефолтные настройки движка (TRD §4.1; method='length' — UI-дефолт прототипа). */
export function makeDefaultSettings(): CuttingSettings {
  return CuttingSettingsSchema.parse({ sheetConfig: DEFAULT_SHEET_CONFIG, method: 'length' });
}

/**
 * Дефолтные кромки UI (project.html:139,146 — толщины 2 / 0.4 мм; названия пустые,
 * в инпутах placeholder'ы). Толщина — UI-only (B-211), не персистится в контракт.
 */
export const DEFAULT_UI_EDGES = {
  edge1: { name: '', thickness: 2 },
  edge2: { name: '', thickness: 0.4 },
} as const;

/** Пустой редактируемый документ раскроя (новый проект без деталей). */
export function makeDefaultDocument(): CuttingDocument {
  return CuttingDocumentSchema.parse({
    meta: { name: '' },
    settings: makeDefaultSettings(),
    parts: [],
    edges: DEFAULT_UI_EDGES,
    offcuts: [], // PR #9-OFFCUTS: новый проект без остатков (CUT-3.1)
  });
}
